export interface SequenceMarking {
  valid: boolean;
  error?: string;
  case?: number;
  sequence_anchor?: number;
  min?: number;
  max?: number;
  equal?: number;
  validation?: string;
  validationException?: 'xau-483-pips' | 'chart-nearest-anchor';
}

export interface SequenceOptions { xauPipException?: boolean; chartWaveFallback?: boolean }
export type Marker = (distance: number, options?: SequenceOptions) => SequenceMarking;
const tidy = (value: number) => Number(value.toFixed(8));
export const PIPS_PER_PRICE = 10;
export const ZONE_PIPS = 50;
export const TP_PIPS = [60, 150] as const;
export const TIMEFRAMES = ['M1', 'M5', 'M15', 'M30', 'H1', 'H4', 'D1'] as const;
export type Timeframe = typeof TIMEFRAMES[number];
export const timeframeLabel = (timeframe: Timeframe) => timeframe === 'D1' ? 'Daily' : timeframe;
// All supported timeframes use the same exact level, with no extra pip buffer.
export const timeframeOffsetPips = (_timeframe: Timeframe) => 0;
export const isTimeframe = (value: unknown): value is Timeframe => TIMEFRAMES.some(t => t === value);

export interface EntryZone {
  name: 'MIN' | 'MAX';
  level: number;
  low: number;
  high: number;
  retracePips: number;
  widthPips: number;
  side: 'SELL' | 'BUY';
  pipSize: number;
}

/** Use the original M5/M15 reference level on every timeframe. */
export function timeframeEntry(zone: EntryZone, _timeframe: Timeframe) {
  return zone.level;
}

/** Manual entries stay inside the same zone on every timeframe. */
export function entryBounds(zone: EntryZone, _timeframe: Timeframe) {
  return { low: zone.low, high: zone.high };
}

export const entryPrice = (value: number, _timeframe: Timeframe) => value.toLocaleString('en-US', {
  useGrouping: false, minimumFractionDigits: 0, maximumFractionDigits: 8,
});

function makeZone(name: 'MIN' | 'MAX', end: number, offset: number, descending: boolean, pipSize: number): EntryZone {
  const direction = descending ? 1 : -1;
  const level = tidy(end + direction * offset);
  const outerEdge = tidy(level + direction * ZONE_PIPS * pipSize);
  return { name, level, low: Math.min(level, outerEdge), high: Math.max(level, outerEdge),
    retracePips: tidy(offset / pipSize), widthPips: ZONE_PIPS, side: descending ? 'SELL' : 'BUY', pipSize };
}

/** TP distances use the user's actual/reference entry, never a combined MIN–MAX range. */
export function calculateTargets(zone: EntryZone, input = String(zone.level), timeframe: Timeframe = 'M5') {
  const entry = Number(input);
  const bounds = entryBounds(zone, timeframe);
  if (!input.trim() || !Number.isFinite(entry) || entry < bounds.low || entry > bounds.high) {
    return { valid: false as const, error: `Enter a price between ${price(bounds.low)} and ${price(bounds.high)} for ${timeframe}.` };
  }
  const direction = zone.side === 'SELL' ? -1 : 1;
  return { valid: true as const, entry,
    tp60: tidy(entry + direction * TP_PIPS[0] * zone.pipSize),
    tp150: tidy(entry + direction * TP_PIPS[1] * zone.pipSize) };
}

/** The original Fibonacci offsets project back from the END of the leg. */
export function calculatePriceZone(from: string, to: string, mark: Marker, pipSize = 1 / PIPS_PER_PRICE, waveScale: 'fixed' | 'scaled' = 'fixed') {
  if (!Number.isFinite(pipSize) || pipSize <= 0) return { valid: false as const, error: 'Invalid pair pip size.' };
  if (!from.trim() || !to.trim()) return { valid: false as const, error: 'Enter both prices to reveal your zones.' };
  const start = Number(from);
  const end = Number(to);
  if (!Number.isFinite(start) || !Number.isFinite(end) || start <= 0 || end <= 0 || start > Number.MAX_SAFE_INTEGER || end > Number.MAX_SAFE_INTEGER) {
    return { valid: false as const, error: 'Enter a valid positive price.' };
  }
  const distance = tidy(Math.abs(start - end));
  if (!distance) return { valid: false as const, error: 'Start and end prices must be different.' };
  const legPips = tidy(distance / pipSize);
  if (!Number.isFinite(legPips) || legPips <= 0 || legPips > Number.MAX_SAFE_INTEGER) {
    return { valid: false as const, error: 'Invalid pip distance.' };
  }
  // XAUUSD uses the actual pip distance: 484.7 pips -> CASE 484, not 48.4.
  // Other pairs retain their existing ten-pip sequence scale. Discard digits
  // beyond the first three without rounding; never change the Z/P prices.
  const goldPips = waveScale === 'fixed' && pipSize === 1 / PIPS_PER_PRICE;
  const [digits, exponent] = tidy(goldPips ? legPips : legPips / 10).toExponential().split('e');
  const firstThree = Number(digits.replace('.', '').slice(0, 3).padEnd(3, '0'));
  const sequenceInput = tidy(firstThree * 10 ** Math.min(0, Number(exponent) - 2));
  // Short gold legs project MIN/MAX directly in pips. For gold legs >=1000
  // pips keep the earlier explicit lock: CASE 100 always uses 340/550 pips,
  // even at 10000/100000 pips. Other pairs keep their existing scale.
  const waveMultiplier = waveScale === 'scaled' ? 10 ** Math.max(0, Number(exponent) - 2) : 1;
  const goldMultiplier = Math.min(10, 10 ** Math.max(0, Number(exponent) - 2));
  const sequenceUnit = pipSize * (goldPips ? goldMultiplier : 10 * waveMultiplier);
  const sequence = mark(sequenceInput, { xauPipException: goldPips && legPips < 1000 });
  if (!sequence.valid) return { valid: false as const, error: sequence.error };
  const descending = start > end;
  const minZone = makeZone('MIN', end, sequence.min! * sequenceUnit, descending, pipSize);
  const maxZone = makeZone('MAX', end, sequence.max! * sequenceUnit, descending, pipSize);
  return { valid: true as const, start, end, distance, legPips,
    descending, sequence, minZone, maxZone };
}

export type PriceZone = Extract<ReturnType<typeof calculatePriceZone>, { valid: true }>;

/** Complete chart markings always project Fibonacci waves, including gaps
 * between 80%-valid anchors. The standalone calculator stays strict.
 * Keep one policy for drag previews, committed lines, monitor and Fit. */
export function calculateChartPriceZone(from: string, to: string, mark: Marker, pipSize = 1 / PIPS_PER_PRICE, waveScale: 'fixed' | 'scaled' = 'fixed') {
  return calculatePriceZone(from, to, (input, options) => mark(input, { ...options, chartWaveFallback: true }), pipSize, waveScale);
}

export const price = (value: number) => value.toLocaleString('en-US', { useGrouping: false, maximumFractionDigits: 8 });
